import { FC, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { convertJsonSchemaToZod } from 'zod-from-json-schema';
import { z } from 'zod';
import {
    MfaProviderIntegrationConfig,
    JsonSchema,
    JsonSchemaProperty,
    UIType,
    ExternalMfaProviderConfigValues
} from '../types';
import TokenRadioGroupField from './TokenRadioGroupField';
import TokenTextField from './TokenTextField';
import TokenDropdown from './TokenDropdown';
import TokenActions from './TokenActions';


type FormType = ExternalMfaProviderConfigValues;
type DefaultValues = Partial<FormType>;

interface ExternalMfaConfigRendererProps {
    config: MfaProviderIntegrationConfig;
    onSave: (data: FormType) => void;
    onCancel: () => void;
    initialData?: DefaultValues;
    saveDisabled?: boolean;
}

interface FieldMetadata {
    fieldId: string;
    title: string;
    required: boolean;
    uiType: UIType;
    property: JsonSchemaProperty;
}

const getErrorMessage = (schema: JsonSchema, fieldId: string): string | undefined => {
    const property = schema.properties?.[fieldId];
    return property?.['x-ui-error-message'];
};

const extractFieldMetadata = (schema: JsonSchema): FieldMetadata[] => {
    const requiredFields = schema.required || [];
    const fields: FieldMetadata[] = [];

    Object.entries(schema.properties || {}).forEach(([fieldId, property]) => {
        fields.push({
            fieldId,
            title: property.title || fieldId,
            required: requiredFields.includes(fieldId),
            uiType: property['x-ui-type'] || 'text',
            property
        });
    });

    return fields;
};

const getDefaultValues = (schema: JsonSchema, initialData: DefaultValues = {}): DefaultValues => {
    const defaultValues: DefaultValues = {};

    Object.entries(schema.properties || {}).forEach(([fieldId, property]) => {
        if (fieldId in initialData) {
            defaultValues[fieldId] = initialData[fieldId];
        } else if (!initialData && property['x-ui-default'] !== undefined) {
            defaultValues[fieldId] = property['x-ui-default'];
        }
    });

    return defaultValues;
};

const ExternalMfaConfigRenderer: FC<ExternalMfaConfigRendererProps> = ({
    config,
    onSave,
    onCancel,
    initialData = {},
    saveDisabled = false
}) => {
    const schema = config.fields;
    const fields = useMemo(() => extractFieldMetadata(schema), [schema]);
    const defaultValues = useMemo(() => getDefaultValues(schema, initialData), [schema, initialData]);

    const zodSchema = useMemo(() => {
        try {
            const schemaWithoutCustomFields = {
                ...schema,
                properties: schema.properties
                    ? Object.fromEntries(
                          Object.entries(schema.properties).map(([key, prop]) => [
                              key,
                              Object.fromEntries(
                                  Object.entries(prop).filter(([propKey]) => !propKey.startsWith('x-ui-'))
                              )
                          ])
                      )
                    : undefined
            };
            
            const baseSchema = convertJsonSchemaToZod(schemaWithoutCustomFields) as unknown as z.ZodTypeAny;
            const requiredFields = schemaWithoutCustomFields.required || [];
            const properties = schemaWithoutCustomFields.properties || {};
            
            let enhancedSchema: z.ZodTypeAny = baseSchema;
            
            if (requiredFields.length > 0) {
                enhancedSchema = baseSchema.superRefine((data, ctx) => {
                    for (const fieldName of requiredFields) {
                        const property = properties[fieldName];
                        const value = (data as any)[fieldName];
                        
                        if (property?.type === 'string') {
                            if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
                                const errorMessage = getErrorMessage(schema, fieldName) || `${fieldName} is required`;
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: errorMessage,
                                    path: [fieldName]
                                });
                            }
                        }
                    }
                }) as z.ZodTypeAny;
            }
            
            return enhancedSchema as unknown as z.ZodType<Partial<FormType>>;
        } catch (error) {
            return z.record(z.any()) as z.ZodType<Partial<FormType>>;
        }
    }, [schema]);

    const safeResolver = useMemo(() => {
        const baseResolver = zodResolver(zodSchema);
        return async (values: any, context: any, options: any) => {
            try {
                return await baseResolver(values, context, options);
            } catch (error: any) {
                if (error?.issues) {
                    const errors: any = {};
                    error.issues.forEach((issue: any) => {
                        const path = issue.path.join('.');
                        errors[path] = {
                            type: issue.code,
                            message: issue.message
                        };
                    });
                    return { values: {}, errors };
                }
                throw error;
            }
        };
    }, [zodSchema]);

    const { control, handleSubmit, formState } = useForm<Partial<FormType>>({
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onChange',
        resolver: safeResolver,
        shouldFocusError: true,
        criteriaMode: 'firstError'
    });

    const hasErrors = Object.keys(formState.errors).length > 0 || formState.isValidating;

    const handleFormSubmit = useCallback(
        (data: FormType) => {
            onSave(data);
        },
        [onSave]
    );

    return (
        <div className="flex flex-col gap-4">
            {fields.map((field) => {
                const { fieldId, title, required, uiType, property } = field;
                const placeholder = property['x-ui-placeholder'] || '';
                const isSecret = property['x-ui-secret'] || false;

                if (uiType === 'text') {
                    return (
                        <Controller
                            key={fieldId}
                            name={fieldId}
                            control={control}
                            render={({ field: { value, onChange }, fieldState }) => {
                                const customErrorMessage = fieldState.error ? getErrorMessage(schema, fieldId) : undefined;
                                return (
                                    <TokenTextField
                                        label={title}
                                        mandatory={required}
                                        value={(value as string) || ''}
                                        onChange={onChange}
                                        placeholder={placeholder}
                                        isSecret={isSecret}
                                        errorMessage={customErrorMessage || fieldState.error?.message}
                                    />
                                );
                            }}
                        />
                    );
                }

                if (uiType === 'radio') {
                    const options = property['x-ui-options'] || [];
                    return (
                        <Controller
                            key={fieldId}
                            name={fieldId}
                            control={control}
                            render={({ field: { value, onChange }, fieldState }) => {
                                const customErrorMessage = fieldState.error ? getErrorMessage(schema, fieldId) : undefined;
                                return (
                                    <TokenRadioGroupField
                                        label={title}
                                        mandatory={required}
                                        selectedOption={value?.toString() || ''}
                                        onChange={onChange}
                                        options={options.map((opt) => ({
                                            title: opt.label,
                                            value: opt.value.toString()
                                        }))}
                                        errorMessage={customErrorMessage || fieldState.error?.message}
                                    />
                                );
                            }}
                        />
                    );
                }

                if (uiType === 'dropdown') {
                    const options =
                        property['x-ui-options'] ||
                        property.enum?.map((val) => ({
                            label: val.toString(),
                            value: val
                        })) ||
                        [];

                    return (
                        <Controller
                            key={fieldId}
                            name={fieldId}
                            control={control}
                            render={({ field: { value, onChange }, fieldState }) => {
                                const customErrorMessage = fieldState.error ? getErrorMessage(schema, fieldId) : undefined;
                                return (
                                    <TokenDropdown
                                        label={title}
                                        placeholder={placeholder}
                                        listOptions={options.map((opt) => ({
                                            title: opt.label,
                                            value: opt.value,
                                            disabled: false,
                                            subtitle: ''
                                        }))}
                                        value={value || ''}
                                        onChange={onChange}
                                        isDisabled={false}
                                        errorMessage={customErrorMessage || fieldState.error?.message}
                                    />
                                );
                            }}
                        />
                    );
                }
                
                return null;
            })}

            <TokenActions
                onSave={handleSubmit(handleFormSubmit)}
                onCancel={onCancel}
                saveDisabled={hasErrors || saveDisabled}
            />
        </div>
    );
};

export default ExternalMfaConfigRenderer;
